import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../providers/AuthProvider';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ReviewAiService from '../../services/ReviewAiService';
import LoadingImage from '../../assets/images/ai_review_generating.png';
import ContentContainer from '../Containers/ContentContainer';
import Section from '../Main/Section/Section';
import ReviewMonitoringService from '../../services/ReviewMonitoringService';
import { Add } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { isValid, parse } from 'date-fns';

const AnalysisCreate = () => {
  const { hasRole } = useContext(AuthContext);
  const [loading, setLoading] = useState<boolean>(false);
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedMonitoring, setSelectedMonitoring] = useState('');
  const [includeDeletedReviews, setIncludeDeletedReviews] = useState('false');
  const [selectedType, setSelectedType] = useState('auto');
  const [monitorings, setMonitorings] = useState([]);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  function onPlatformChange(event: SelectChangeEvent) {
    setSelectedMonitoring(event.target.value);
  }

  async function createAnalysis() {
    await ReviewAiService.create(
      selectedMonitoring,
      selectedType,
      includeDeletedReviews === 'true',
      fromDate,
      toDate
    );
    navigate('/monitoring/analysis');
  }

  const getMonitorings = async () => {
    const controller = new AbortController();
    const data = await ReviewMonitoringService.getMonitoring(
      true,
      'both',
      setLoading,
      controller.signal
    );
    setMonitorings(data);
  };

  useEffect(() => {
    getMonitorings();
  }, []);

  return hasRole('can-use-aireviews') ? (
    <Section>
      <ContentContainer>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            borderRadius: `${theme.shape.borderRadius}px`,
            backgroundColor: theme.palette.background.paper,
            padding: '5rem 0',
          }}
        >
          <img
            src={LoadingImage}
            alt="No content"
            style={{ width: '30rem', marginBottom: '2rem', maxWidth: '100%', height: 'auto' }}
          />
          <br />
          <FormControl
            size="small"
            sx={{ minWidth: '284px', my: theme.spacing(1) }}
          >
            <InputLabel id="platform-select-label">{t('platform')}</InputLabel>
            <Select
              labelId="platform-select-label"
              value={selectedMonitoring}
              onChange={onPlatformChange}
              label={t('platform')}
            >
              <MenuItem value="">
                <em>-</em>
              </MenuItem>
              {monitorings &&
                monitorings.map(({ id, name, label }) => {
                  return (
                    <MenuItem
                      value={id}
                      key={id}
                    >
                      {label}
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>
          <FormControl
            size="small"
            sx={{ minWidth: '284px', my: theme.spacing(1) }}
          >
            <InputLabel id="include-deleted-reviews-select-label">
              {t('include_deleted_reviews')}
            </InputLabel>
            <Select
              labelId="include-deleted-reviews-select-label"
              value={includeDeletedReviews}
              onChange={(event) => setIncludeDeletedReviews(event.target.value)}
              label={t('include_deleted_reviews')}
            >
              <MenuItem value={'false'}>{t('no')}</MenuItem>
              <MenuItem value={'true'}>{t('yes')}</MenuItem>
            </Select>
          </FormControl>
          {hasRole('can-use-aireviewtype') && (
            <FormControl
              size="small"
              sx={{ minWidth: '284px', my: theme.spacing(1) }}
            >
              <InputLabel id="type-select-label">{t('aireviewtype')}</InputLabel>
              <Select
                labelId="platform-select-label"
                value={selectedType}
                onChange={(ev) => setSelectedType(ev.target.value)}
                label={t('platform')}
              >
                <MenuItem
                  value={'auto'}
                  key={'auto'}
                >
                  {t('aireviewauto')}
                </MenuItem>
                <MenuItem
                  value={'file'}
                  key={'file'}
                >
                  {t('aireviewfile')}
                </MenuItem>
                <MenuItem
                  value={'messages'}
                  key={'messages'}
                >
                  {t('aireviewmessages')}
                </MenuItem>
              </Select>
            </FormControl>
          )}
          <Box
            component="form"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              width: '284px',
              marginTop: '40px',
            }}
            noValidate
          >
            <Typography variant={'body2'}>
              <strong>{t('date').toUpperCase()}</strong> ({t('optional_configuration')})
            </Typography>
            <DatePicker
              disableFuture
              label={t('date_from')}
              value={
                fromDate
                  ? (() => {
                      const d = parse(fromDate, 'yyyy-MM-dd', new Date());
                      return isValid(d) ? d : null;
                    })()
                  : null
              }
              format="dd.MM.yyyy"
              onChange={(newValue) => {
                setFromDate(newValue ? dayjs(newValue).format('YYYY-MM-DD') : '');
              }}
              slotProps={{
                textField: {
                  error: false,
                  sx: !fromDate
                    ? {
                        '& input': {
                          color: 'text.disabled', // or a specific grey
                        },
                      }
                    : undefined,
                },
              }}
            />
            <DatePicker
              disableFuture
              label={t('date_to')}
              value={
                toDate
                  ? (() => {
                      const d = parse(toDate, 'yyyy-MM-dd', new Date());
                      return isValid(d) ? d : null;
                    })()
                  : null
              }
              format="dd.MM.yyyy"
              onChange={(newValue) => {
                setToDate(newValue ? dayjs(newValue).format('YYYY-MM-DD') : '');
              }}
              slotProps={{
                textField: {
                  error: false,
                  sx: !toDate
                    ? {
                        '& input': {
                          color: 'text.disabled', // or a specific grey
                        },
                      }
                    : undefined,
                },
              }}
            />
          </Box>
          <Button
            variant="contained"
            disabled={!selectedMonitoring || loading}
            startIcon={<Add />}
            onClick={() => createAnalysis()}
            sx={{ mt: theme.spacing(2) }}
          >
            {t('new_analysis')}
          </Button>
          {loading && <LinearProgress></LinearProgress>}
        </Box>
      </ContentContainer>
    </Section>
  ) : (
    <></>
  );
};

export default AnalysisCreate;
